import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { http } from "@/lib/api";
import PublicLayout from "@/components/PublicLayout";
import { Users, Bed, Bath, ArrowUpRight } from "lucide-react";
import { useLang } from "@/lib/i18n";
import { resolveImg, onImgError } from "@/lib/images";
import useSEO from "@/lib/useSEO";

export default function Properties() {
  const { t, lang } = useLang();
  useSEO({
    lang, path: "/proprieta",
    title: lang === "en" ? "Rooms & Lofts — Il Melograno apARTments" : "Camere & Loft — Il Melograno apARTments",
    description: lang === "en"
      ? "Choose an en-suite room or a self-contained loft. New furnishings, air conditioning, Wi-Fi, Chromecast. All rooms in Castellanza (VA)."
      : "Scegli la stanza con bagno privato o un loft indipendente. Arredi nuovi, aria condizionata, Wi-Fi, Chromecast. Tutti gli alloggi a Castellanza (VA).",
  });
  const [props, setProps] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    http.get("/properties?active_only=true")
      .then((r) => setProps(r.data))
      .finally(() => setLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- runs once on mount

  const filtered = props.filter((p) => filter === "all" ? true : p.type === filter);

  return (
    <PublicLayout>
      <section className="mel-container pt-16 pb-8">
        <div className="eyebrow mb-4">{t.properties.eyebrow}</div>
        <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl tracking-tight leading-tight max-w-3xl break-words">
          {t.properties.titleA} <em className="text-[var(--mel-primary)]">{t.properties.titleB}</em>{t.properties.titleC}
        </h1>
        <p className="mt-6 text-[var(--mel-muted)] max-w-2xl text-lg">{t.properties.subtitle}</p>

        <div className="mt-10 flex flex-wrap gap-3" data-testid="prop-filters">
          {[{ k: "all", l: t.properties.all }, { k: "camera", l: t.properties.rooms }, { k: "loft", l: t.properties.lofts }].map((f) => (
            <button
              key={f.k}
              onClick={() => setFilter(f.k)}
              data-testid={`filter-${f.k}`}
              className={`text-sm px-5 py-2 rounded-full border transition-colors ${
                filter === f.k
                  ? "bg-[var(--mel-primary)] text-white border-[var(--mel-primary)]"
                  : "border-[var(--mel-border)] hover:border-[var(--mel-primary)]"
              }`}
            >
              {f.l}
            </button>
          ))}
        </div>
      </section>

      <section className="mel-container pb-24">
        {loading ? (
          <div className="py-24 text-center text-[var(--mel-muted)]" data-testid="prop-loading">{t.common.loading}</div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="prop-grid">
            {filtered.map((p) => (
              <Link
                to={`/proprieta/${p.property_id}`}
                key={p.property_id}
                className="group block hover-lift bg-white border border-[var(--mel-border)] rounded-md overflow-hidden"
                data-testid={`prop-card-${p.property_id}`}
              >
                <div className="relative aspect-[4/5] overflow-hidden">
                  <img
                    src={resolveImg(p.images?.[0])}
                    alt={`${p.name} — ${p.type === "loft" ? "Loft" : "Camera"} a Castellanza`}
                    onError={onImgError}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute top-4 left-4 bg-[var(--mel-bg)]/95 text-xs px-3 py-1 rounded-full uppercase tracking-widest">
                    {p.type === "loft" ? t.properties.loft : t.properties.room}
                  </div>
                </div>
                <div className="p-5 md:p-6">
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0">
                      <h3 className="font-serif text-xl">{p.name}</h3>
                      <p className="text-sm text-[var(--mel-muted)] mt-1 line-clamp-2">{(lang === "en" && p.short_description_en) ? p.short_description_en : p.short_description}</p>
                    </div>
                    <ArrowUpRight size={20} className="text-[var(--mel-primary)] mt-1 flex-shrink-0 transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
                  </div>
                  <div className="mt-5 pt-4 border-t border-[var(--mel-border)] flex items-center gap-4 text-xs text-[var(--mel-muted)]">
                    <span className="flex items-center gap-1.5"><Users size={14} /> {p.max_guests}</span>
                    <span className="flex items-center gap-1.5"><Bed size={14} /> {p.beds}</span>
                    <span className="flex items-center gap-1.5"><Bath size={14} /> {p.baths}</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>
    </PublicLayout>
  );
}
